import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  CAMPAIGN_PURPOSES, type CampaignPurpose, purposeText,
} from "@/lib/campaign";
import { useFormChanged, zodResolver } from "@/lib/form";
import { useCreateCampaign, useTenants, useUpdateCampaign } from "@/lib/queries";
import type { Campaign } from "@/lib/types";

/**
 * Create / edit a campaign's identity: a firm + state + language.
 *
 * The slug is the CRM's firm tag (`lhpark_ca`), and it is what intake will resolve to this row
 * — so it is shown as the identifier it is, lowercase and fixed-width, rather than generated
 * from the name behind the user's back.
 *
 * There is no queue to pick. Creating a campaign creates its primary line, named after the slug,
 * and core-api refuses a client that points the campaign at any other line (ADR-014), so a picker
 * here could only offer choices that fail. How the campaign is worked — its lines, staffing,
 * dispositions, dialing and lists — is on its own page, which is where a create lands.
 */

const schema = z.object({
  name: z.string().min(1, "Required").max(200),
  slug: z
    .string()
    .min(1, "Required")
    .max(63)
    .regex(/^[a-z0-9][a-z0-9_-]*$/, "Lowercase letters, digits, underscore and hyphen only"),
  state: z
    .string()
    .max(2)
    .regex(/^([A-Z]{2})?$/, "Two-letter state code, e.g. CA")
    .optional(),
  language: z.enum(["en", "es"]),
  purpose: z.enum(CAMPAIGN_PURPOSES),
  enabled: z.boolean(),
  tenant_id: z.number().nullable(),
});
type FormValues = z.infer<typeof schema>;

export function CampaignFormDialog({
  open, onOpenChange, campaign, onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  campaign?: Campaign;
  /** After a create, with the new row: the list page opens it. */
  onCreated?: (campaign: Campaign) => void;
}) {
  const editing = Boolean(campaign);
  const { user: actor } = useAuth();
  const toast = useToast();
  const create = useCreateCampaign();
  const update = useUpdateCampaign();
  // Only a superadmin picks a tenant; everyone else's is forced by the server.
  const isSuperadmin = actor?.role === "superadmin";
  const { data: tenants } = useTenants({ enabled: open && isSuperadmin });
  const [formError, setFormError] = React.useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "", slug: "", state: "", language: "en", purpose: "both", enabled: true,
      tenant_id: actor?.tenant_id ?? null,
    },
  });
  const changed = useFormChanged(form);

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    form.reset(
      campaign
        ? {
            name: campaign.name,
            slug: campaign.slug,
            state: campaign.state ?? "",
            language: campaign.language === "es" ? "es" : "en",
            purpose: (CAMPAIGN_PURPOSES as readonly string[]).includes(campaign.purpose)
              ? (campaign.purpose as CampaignPurpose)
              : "both",
            enabled: campaign.enabled,
            tenant_id: campaign.tenant_id,
          }
        : {
            name: "", slug: "", state: "", language: "en", purpose: "both", enabled: true,
            // With one tenant the server fills it in; the picker below is for when there are more.
            tenant_id: actor?.tenant_id ?? (tenants?.length === 1 ? tenants[0]!.id : null),
          },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaign, actor?.tenant_id, tenants]);

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    const payload = {
      name: values.name.trim(),
      slug: values.slug,
      state: values.state?.trim() || null,
      language: values.language,
      enabled: values.enabled,
      ...(isSuperadmin && values.tenant_id !== null ? { tenant_id: values.tenant_id } : {}),
    };
    try {
      if (campaign) {
        await update.mutateAsync({ id: campaign.id, ...payload });
        toast.success("Campaign updated", payload.name);
        onOpenChange(false);
      } else {
        const created = await create.mutateAsync(payload);
        toast.success("Campaign created", payload.name);
        onOpenChange(false);
        onCreated?.(created);
      }
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the campaign.");
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{editing ? "Edit campaign" : "New campaign"}</DialogTitle>
          <DialogDescription>
            {editing
              ? "The firm, its CRM tag, and the language its leads speak."
              : "A firm's intake programme. Creating one creates the line its calls wait in; the rest is set up on its page."}
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            <Field
              label="Name"
              htmlFor="campaign-name"
              required
              error={form.formState.errors.name?.message}
            >
              <Input
                autoComplete="off"
                placeholder="Larry H Parker — CA"
                invalid={Boolean(form.formState.errors.name)}
                {...form.register("name")}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-[1fr_6rem_7rem]">
              <Field
                label="Slug"
                htmlFor="campaign-slug"
                required
                hint={
                  editing
                    ? "The CRM's firm tag. Renaming it keeps the line's name, which calls already use."
                    : "The CRM's firm tag. Unique within the organisation."
                }
                error={form.formState.errors.slug?.message}
              >
                <Input
                  className="font-mono"
                  autoComplete="off"
                  placeholder="lhpark_ca"
                  invalid={Boolean(form.formState.errors.slug)}
                  {...form.register("slug")}
                />
              </Field>
              <Field
                label="State"
                htmlFor="campaign-state"
                hint="US state."
                error={form.formState.errors.state?.message}
              >
                <Input
                  className="font-mono uppercase"
                  autoComplete="off"
                  placeholder="CA"
                  maxLength={2}
                  invalid={Boolean(form.formState.errors.state)}
                  {...form.register("state", {
                    setValueAs: (v: string) => v.toUpperCase(),
                  })}
                />
              </Field>
              <Field label="Language" htmlFor="campaign-language">
                <Select
                  value={form.watch("language")}
                  onValueChange={(v) => form.setValue("language", v as "en" | "es")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field
              label="This campaign is for"
              htmlFor="campaign-purpose"
              hint="What it shows you. It does not change how calls are routed or dialed — a campaign with a dial mode keeps dialing whichever you pick."
            >
              <Select
                value={form.watch("purpose")}
                onValueChange={(v) => form.setValue("purpose", v as CampaignPurpose)}
              >
                <SelectTrigger id="campaign-purpose">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CAMPAIGN_PURPOSES.map((p) => (
                    <SelectItem key={p} value={p} description={purposeText(p).description}>
                      {purposeText(p).label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {isSuperadmin && !editing && (tenants?.length ?? 0) > 1 && (
              <Field label="Tenant" htmlFor="campaign-tenant" required>
                <Select
                  value={form.watch("tenant_id")?.toString() ?? ""}
                  onValueChange={(v) => form.setValue("tenant_id", Number(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {(tenants ?? []).map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
              <div>
                <p className="text-xs font-medium text-text">Enabled</p>
                <p className="text-xs text-text-muted">
                  A disabled campaign keeps its numbers, but calls to them hang up.
                </p>
              </div>
              <Switch
                checked={form.watch("enabled")}
                onCheckedChange={(v) => form.setValue("enabled", v)}
                aria-label="Enabled"
              />
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={form.formState.isSubmitting}>
              {editing ? "Save changes" : "Create campaign"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
